'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Language } from '@/lib/i18n';

interface Country {
  code: string;
  name: string;
  flag: string;
  languages: { code: Language; label: string }[];
}

const COUNTRIES: Country[] = [
  {
    code: 'MR',
    name: 'Mauritanie',
    flag: '🇲🇷',
    languages: [
      { code: 'fr', label: 'Français' },
      { code: 'ar', label: 'العربية' },
    ],
  },
  {
    code: 'SN',
    name: 'Sénégal',
    flag: '🇸🇳',
    languages: [
      { code: 'fr', label: 'Français' },
      { code: 'en', label: 'English' },
    ],
  },
  {
    code: 'ML',
    name: 'Mali',
    flag: '🇲🇱',
    languages: [
      { code: 'fr', label: 'Français' },
      { code: 'en', label: 'English' },
    ],
  },
  {
    code: 'CI',
    name: "Côte d'Ivoire",
    flag: '🇨🇮',
    languages: [
      { code: 'fr', label: 'Français' },
      { code: 'en', label: 'English' },
    ],
  },
  {
    code: 'BF',
    name: 'Burkina Faso',
    flag: '🇧🇫',
    languages: [
      { code: 'fr', label: 'Français' },
      { code: 'en', label: 'English' },
    ],
  },
];

export default function CountrySelector() {
  const { language, setLanguage, setCountry } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    // Default to French when switching country
    const defaultLang = country.languages[0].code;
    setLanguage(defaultLang);
    setCountry(country.code);
    setOpen(false);
  };

  const handleLangSelect = (langCode: Language) => {
    setLanguage(langCode);
  };

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      {/* Country dropdown trigger */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dispatch-surface bg-dispatch-surface/50 backdrop-blur-md text-xs font-semibold font-mono text-dispatch-muted hover:text-dispatch-text hover:border-dispatch-cyan/40 transition-all duration-200"
        aria-label="Select country"
      >
        <span className="text-base leading-none">{selectedCountry.flag}</span>
        <span className="hidden sm:inline tracking-wide">{selectedCountry.code}</span>
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Language tabs */}
      <div className="flex items-center gap-0.5 bg-dispatch-surface/50 backdrop-blur-md border border-dispatch-surface rounded-full px-1 py-1">
        {selectedCountry.languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleLangSelect(lang.code)}
            className={`px-2.5 py-0.5 rounded-full text-xs font-bold font-mono tracking-wide transition-all duration-200 ${
              language === lang.code
                ? 'bg-dispatch-cyan text-dispatch-bg' :'text-dispatch-muted hover:text-dispatch-text'
            }`}
          >
            {lang.code === 'ar' ? 'AR' : lang.code === 'fr' ? 'FR' : 'EN'}
          </button>
        ))}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute top-full right-0 mt-2 w-52 bg-dispatch-bg border border-dispatch-surface rounded-2xl shadow-2xl overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-dispatch-surface">
            <p className="text-xs font-mono text-dispatch-muted uppercase tracking-widest">Pays / Country</p>
          </div>
          <ul className="py-1">
            {COUNTRIES.map((country) => (
              <li key={country.code}>
                <button
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ${
                    selectedCountry.code === country.code
                      ? 'bg-dispatch-cyan/10 text-dispatch-cyan' :'text-dispatch-text hover:bg-dispatch-surface/60'
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span>{country.name}</span>
                  {selectedCountry.code === country.code && (
                    <svg className="ml-auto w-4 h-4 text-dispatch-cyan" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
